import React, { useState, useEffect, useContext } from 'react'
import './style.scss'
import { useQuery } from 'react-query'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import Icon from 'react-icons-kit'
import {
    users, heart, clock, send, fileText, shield,
    checkCircle, alertCircle, calendar, eye, award,
    edit2, camera, zap, arrowRight, creditCard, trendingUp
} from 'react-icons-kit/feather'
import { Images } from '../../../utils/Images'
import Requests from '../../../utils/Requests/Index'
import ImageCrop from '../../../components/Form/Picture'
import Slider from '../../../components/SliderShow/index'
import VisitorCard, { isVisitorOnline } from '../../../components/Dashboard/VisitorCard'
import Loading from '../../../components/Loading/Index'
import { singleProContext } from '../../../utils/context/SingleProfileContext'

const Index = () => {
    let { search } = useLocation()
    let navigate = useNavigate()
    const { handleLocalStoreIds } = useContext(singleProContext);
    const [show, setShow] = useState(false)
    const [visitorFilter, setVisitorFilter] = useState('all')
    const [header] = useState({ headers: { Authorization: "Bearer " + localStorage.getItem('token') } })
    const [pageLoad, setPageload] = useState(true)

    // Fetch data
    const getDashboardData = async () => {
        try {
            const data = await Requests.Dashboard.Index(header)
            if (data && data.user) {
                setPageload(false)
            }
            return data?.user
        } catch (error) {
            console.log(error.message)
        }
    }

    const { data, isLoading, isError } = useQuery("dashboard", getDashboardData, {
        retry: false,
        cacheTime: 1200000,
        refetchOnWindowFocus: false,
        enabled: Boolean(pageLoad)
    })

    // after payment (package purchase redirect)
    useEffect(() => {
        const status = search ? new URLSearchParams(search).get('status') : null
        if (status === "success") {
            toast.success('Congratulations,Payment Successful')
        }
        else if (status === 'failed') {
            toast.error('Payment is not Successful, try again')
        }
        else if (status === 'cancle') {
            toast.error('Payment cancled, try again')
        }
    }, [search])

    // Set ids For Single Profile...
    useEffect(() => {
        let recentViewIds = []
        let matchIds = []
        if (data?.recentViewed) {
            recentViewIds = data.recentViewed.map(info => info._id);
        }
        if (data?.matches) {
            matchIds = data.matches.map(info => info._id);
        }
        handleLocalStoreIds([...recentViewIds, ...matchIds]);
    }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

    const toPackage = e => {
        if (e) e.preventDefault()
        navigate('/my-ghotokbari/package')
    }

    if (isLoading || isError) return <Loading />

    const isFree = data?.profileType === 'Free'
    const profileImage = data?.image ? data.image : Images.NoImage
    const proposalLimit = data?.packageInfo?.totalLimit
    const contactViewLimit = data?.packageInfo?.contactViewLimit
    const expireDate = data?.packageExpire ? String(data.packageExpire).split("T")[0] : null
    const updateRange = Math.min(100, Math.max(0, Number(data?.updateRange) || 0))
    const matches = data?.matches || []
    const recentViewed = data?.recentViewed || []

    // API stores recent views oldest -> newest, so the latest visitors come first
    const visitors = [...recentViewed].reverse()
    const onlineVisitors = visitors.filter(isVisitorOnline)
    const recentVisitors = (visitorFilter === 'online' ? onlineVisitors : visitors).slice(0, 8)

    const stats = [
        { key: 'connection', label: 'Connections', icon: users, to: '/connection' },
        { key: 'favorite', label: 'Favorites', icon: heart, to: null },
        { key: 'pendingRequest', label: 'Pending Requests', icon: clock, to: '/connection/pending' },
        { key: 'sentRequest', label: 'Sent Proposals', icon: send, to: '/connection/send' },
        { key: 'posts', label: 'Timeline Posts', icon: fileText, to: '/timeline' },
        { key: 'block', label: 'Blocked Users', icon: shield, to: null },
    ]

    const upgrade = <span className="upgrade-link" onClick={toPackage}>Upgrade</span>

    return (
        <div className="dashboard">

            {/* ================= Profile summary ================= */}
            <section className="dashboard-hero">
                <div className="container">
                    <div className="hero-inner">
                        <div className="hero-avatar">
                            <img src={profileImage} alt={data.name} />
                            <button
                                type="button"
                                className="avatar-edit"
                                title="Change profile picture"
                                onClick={() => setShow(true)}
                            >
                                <Icon icon={camera} size={16} />
                            </button>
                        </div>

                        <div className="hero-info">
                            <h4 className="text-capitalize">{data.name}</h4>
                            <p className="hero-id">ID: {data.profileId}</p>

                            <div className="hero-badges">
                                <span className={`badge-pill ${isFree ? 'free' : 'premium'}`}>
                                    <Icon icon={award} size={13} /> {data.profileType} Membership
                                </span>
                                <span className={`badge-pill ${data.isPhoneVerified ? 'verified' : 'unverified'}`}>
                                    <Icon icon={data.isPhoneVerified ? checkCircle : alertCircle} size={13} />
                                    {data.isPhoneVerified ? 'Verified number' : 'Unverified number'}
                                </span>
                            </div>
                        </div>

                        <div className="hero-actions">
                            <Link to="/my-ghotokbari/profile" className="btn-ghost">
                                <Icon icon={edit2} size={15} /> My Profile
                            </Link>
                            <button type="button" className="btn-solid" onClick={toPackage}>
                                <Icon icon={zap} size={15} /> {isFree ? 'Upgrade Package' : 'My Package'}
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <div className="dashboard-body">
                <div className="container">

                    {/* ================= Activity stats ================= */}
                    <div className="stats-grid">
                        {stats.map((stat, i) => {
                            const card = (
                                <div className="stat-card">
                                    <div className="stat-icon"><Icon icon={stat.icon} size={20} /></div>
                                    <div className="stat-content">
                                        <h4>{data?.[stat.key] || 0}</h4>
                                        <p>{stat.label}</p>
                                    </div>
                                </div>
                            )

                            return stat.to
                                ? <Link to={stat.to} className="stat-link" key={i}>{card}</Link>
                                : <div className="stat-link" key={i}>{card}</div>
                        })}
                    </div>

                    <div className="row">
                        {/* ================= Account overview ================= */}
                        <div className="col-12 col-xl-5 mb-4 mb-xl-0">
                            <div className="dashboard-card h-100">
                                <div className="dashboard-card-header">
                                    <h5><Icon icon={creditCard} size={16} /> Account Overview</h5>
                                </div>

                                <div className="dashboard-card-body">
                                    <div className="detail-list">
                                        <div className="detail-row">
                                            <span className="label">
                                                <span className="label-icon"><Icon icon={award} size={15} /></span>
                                                Account Type
                                            </span>
                                            <span className="value">{data.profileType} Membership</span>
                                        </div>

                                        <div className="detail-row">
                                            <span className="label">
                                                <span className="label-icon"><Icon icon={heart} size={15} /></span>
                                                Proposal limit
                                            </span>
                                            <span className="value">{proposalLimit ? proposalLimit : upgrade}</span>
                                        </div>

                                        <div className="detail-row">
                                            <span className="label">
                                                <span className="label-icon"><Icon icon={eye} size={15} /></span>
                                                Contact view
                                            </span>
                                            <span className="value">
                                                {isFree ? upgrade : (contactViewLimit ?? 0)}
                                            </span>
                                        </div>

                                        <div className="detail-row">
                                            <span className="label">
                                                <span className="label-icon"><Icon icon={calendar} size={15} /></span>
                                                Expire date
                                            </span>
                                            <span className="value">
                                                {isFree
                                                    ? upgrade
                                                    : (expireDate || 'N/A')
                                                }
                                            </span>
                                        </div>

                                        <div className="detail-row">
                                            <span className="label">
                                                <span className="label-icon"><Icon icon={checkCircle} size={15} /></span>
                                                Mobile verification
                                            </span>
                                            <span className={`value ${data.isPhoneVerified ? 'verified' : 'unverified'}`}>
                                                {data.isPhoneVerified ? 'Verified' : 'Unverified'}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Profile completeness */}
                                    <div className="progress-block">
                                        <div className="progress-head">
                                            <h6><Icon icon={trendingUp} size={15} /> Profile completeness</h6>
                                            <span className="progress-value">{updateRange}%</span>
                                        </div>
                                        <div className="progress">
                                            <div
                                                className="progress-bar"
                                                role="progressbar"
                                                style={{ width: updateRange + "%" }}
                                                aria-valuenow={updateRange}
                                                aria-valuemin="0"
                                                aria-valuemax="100"
                                            />
                                        </div>
                                        <p>Profile improve steps ( ex: contact details, id card information)</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ================= My matches ================= */}
                        <div className="col-12 col-xl-7">
                            <div className="dashboard-card h-100">
                                <div className="dashboard-card-header">
                                    <h5>
                                        <Icon icon={users} size={16} /> My Matches
                                        <span className="count-badge">{matches.length}</span>
                                    </h5>
                                    <Link to="/matches/all" className="view-all">
                                        View all <Icon icon={arrowRight} size={13} />
                                    </Link>
                                </div>

                                <div className="dashboard-card-body">
                                    {matches.length ?
                                        <div className="profile-slider">
                                            <Slider items={matches} />
                                        </div>
                                        :
                                        <div className="empty-state">
                                            <Icon icon={users} size={30} />
                                            <p>No matches are available as your partner preference.</p>
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ================= Recent visitors ================= */}
                    <div className="dashboard-card visitors-card mt-4">
                        <div className="dashboard-card-header">
                            <div className="header-left">
                                <span className="header-icon"><Icon icon={eye} size={17} /></span>
                                <div className="header-text">
                                    <h5>
                                        Recent Visitors
                                        <span className="count-badge">{recentViewed.length}</span>
                                    </h5>
                                    <p className="header-sub">Members who viewed your profile recently</p>
                                </div>
                            </div>

                            <div className="header-right">
                                <div className="segmented">
                                    <button
                                        type="button"
                                        className={visitorFilter === 'all' ? "active" : ""}
                                        onClick={() => setVisitorFilter('all')}
                                    >
                                        All
                                    </button>
                                    <button
                                        type="button"
                                        className={visitorFilter === 'online' ? "active" : ""}
                                        onClick={() => setVisitorFilter('online')}
                                    >
                                        Active
                                        {onlineVisitors.length ? <i>{onlineVisitors.length}</i> : null}
                                    </button>
                                </div>

                                <Link to="/matches/recently-viewed" className="view-all">
                                    View all <Icon icon={arrowRight} size={13} />
                                </Link>
                            </div>
                        </div>

                        <div className="dashboard-card-body">
                            {recentVisitors.length ?
                                <div className="visitor-grid">
                                    {recentVisitors.map((item, i) => <VisitorCard item={item} key={item._id || i} />)}
                                </div>
                                :
                                <div className="empty-state">
                                    <Icon icon={eye} size={30} />
                                    <p>
                                        {visitorFilter === 'online'
                                            ? "No active visitors right now."
                                            : "No one has viewed your profile yet."}
                                    </p>
                                    {visitorFilter === 'online'
                                        ? <button type="button" className="empty-action" onClick={() => setVisitorFilter('all')}>
                                            Show all visitors
                                        </button>
                                        : <Link to="/matches/all" className="empty-action">
                                            Browse members <Icon icon={arrowRight} size={13} />
                                        </Link>
                                    }
                                </div>
                            }
                        </div>
                    </div>

                    {/* ================= Add sense ================= */}
                    {data.addsense ?
                        <div className="addsense-card mt-4">
                            <img src={data.addsense} className="img-fluid" alt="Add sense" />
                        </div>
                        : null}

                </div>
            </div>

            {show ? <ImageCrop show={show} onHide={() => setShow(false)} image={data.image ? data.image : null} setPageload={setPageload} /> : null}
        </div>
    );
};

export default Index;
